import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUsers, faMusic, faMicrophone, faCalendar, faMapMarkerAlt, faUser, faArrowLeft, faEdit, faTrash, faCheck, faTimes, faPlay, faStop, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import api from '@/services/api'
import Spinner from '@/components/Spinner'
import { ConfirmModal } from '@/components/ConfirmModal'
import { StatusBadge, PlanTypeBadge } from '@/utils/badges'

/**
 * Event interface
 */
interface EventDetail {
  id: string
  organizerId: string
  organizer: {
    id: string
    email: string
    name: string
    organization: string | null
    subscriptionType: string
    subscriptionStatus: string
  } | null
  name: string
  description: string | null
  date: string | null
  status: 'draft' | 'active' | 'completed' | 'cancelled' | 'hidden'
  planType: 'free' | 'one_shot' | 'subscription'
  venue: {
    id: string
    name: string
    address: string | null
    city: string
    country: string | null
  } | null
  singersCount: number
  songsCount: number
  singers: Array<{
    id: string
    pseudo: string
    name: string
    isGuest: boolean
    songsCount: number
    joinedAt: string
  }>
  songs: Array<{
    id: string
    title: string
    artist: string
    position: number
    status: string
    createdAt: string
  }>
  createdAt: string
  updatedAt: string
}

/**
 * Event Details Page
 * Dark mode geek aesthetic - Desktop only
 */
export default function EventDetailsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [event, setEvent] = useState<EventDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    action: 'status' | 'delete' | null
    targetStatus?: 'draft' | 'active' | 'completed' | 'cancelled' | 'hidden'
    isLoading: boolean
  }>({
    isOpen: false,
    action: null,
    isLoading: false,
  })

  // Fetch event details
  useEffect(() => {
    if (!id) {
      navigate('/events')
      return
    }

    const fetchEventDetails = async () => {
      try {
        const response = await api.get(`/superadmin/events/${id}`)
        if (response.data.success) {
          setEvent(response.data.data)
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    fetchEventDetails()
  }, [id, navigate, t])

  // Refresh event details after update
  const refreshEventDetails = async () => {
    if (!id) return
    try {
      const response = await api.get(`/superadmin/events/${id}`)
      if (response.data.success) {
        setEvent(response.data.data)
      }
    } catch (err: any) {
      console.error('Failed to refresh event details:', err)
    }
  }

  // Handle status change
  const handleStatusChange = (newStatus: 'draft' | 'active' | 'completed' | 'cancelled' | 'hidden') => {
    setModalState({
      isOpen: true,
      action: 'status',
      targetStatus: newStatus,
      isLoading: false,
    })
  }

  // Handle delete
  const handleDelete = () => {
    setModalState({
      isOpen: true,
      action: 'delete',
      isLoading: false,
    })
  }

  // Confirm action
  const handleConfirmAction = async () => {
    if (!event || !modalState.action) return

    setModalState((prev) => ({ ...prev, isLoading: true }))

    try {
      if (modalState.action === 'status' && modalState.targetStatus) {
        // Update event status via superadmin endpoint
        await api.put(`/superadmin/events/${event.id}/status`, { status: modalState.targetStatus })
        await refreshEventDetails()
        setError(null)
      } else if (modalState.action === 'delete') {
        // Delete event via superadmin endpoint
        await api.delete(`/superadmin/events/${event.id}`)
        navigate('/events')
        return
      }

      setModalState({ isOpen: false, action: null, isLoading: false })
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || t('common.error'))
      setModalState((prev) => ({ ...prev, isLoading: false }))
    }
  }

  // Cancel action
  const handleCancelAction = () => {
    setModalState({ isOpen: false, action: null, isLoading: false })
  }


  // Define detail sections
  const detailSections = [
    {
      key: 'identity',
      label: 'Informations générales',
      rows: [
        { key: 'name', label: 'Nom', value: event?.name || '—' },
        { key: 'description', label: 'Description', value: event?.description || '—' },
        {
          key: 'date',
          label: 'Date',
          value: event?.date
            ? new Date(event.date).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : '—',
        },
        {
          key: 'status',
          label: 'Statut',
          value: event ? <StatusBadge status={event.status} /> : '—',
        },
        {
          key: 'planType',
          label: 'Type de plan',
          value: event ? <PlanTypeBadge planType={event.planType} variant="badge" /> : '—',
        },
        {
          key: 'createdAt',
          label: 'Date de création',
          value: event ? new Date(event.createdAt).toLocaleDateString('fr-FR') : '—',
        },
      ],
    },
    {
      key: 'organizer',
      label: 'Organisateur',
      rows: [
        {
          key: 'organizerName',
          label: 'Nom',
          value: event?.organizer?.name || '—',
        },
        {
          key: 'organizerEmail',
          label: 'Email',
          value: event?.organizer?.email || '—',
        },
        {
          key: 'organizerOrganization',
          label: 'Organisation',
          value: event?.organizer?.organization || '—',
        },
        {
          key: 'organizerSubscription',
          label: 'Plan',
          value: (() => {
            const subType = event?.organizer?.subscriptionType || 'free'
            return subType === 'free' ? 'Gratuit' : subType
          })(),
        },
        {
          key: 'organizerLink',
          label: 'Actions',
          value: event ? (
            <button
              onClick={() => navigate(`/organizers/${event.organizerId}`)}
              className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-lg transition text-xs font-mono"
            >
              Voir organisateur
            </button>
          ) : (
            '—'
          ),
        },
      ],
    },
    {
      key: 'venue',
      label: 'Lieu',
      rows: event?.venue
        ? [
            { key: 'venueName', label: 'Nom', value: event.venue.name || '—' },
            { key: 'venueAddress', label: 'Adresse', value: event.venue.address || '—' },
            { key: 'venueCity', label: 'Ville', value: event.venue.city || '—' },
            { key: 'venueCountry', label: 'Pays', value: event.venue.country || '—' },
            {
              key: 'venueLink',
              label: 'Actions',
              value: event.venue ? (
                <button
                  onClick={() => {
                    if (event.venue) {
                      navigate(`/venues/${event.venue.id}`)
                    }
                  }}
                  className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-lg transition text-xs font-mono flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-xs" />
                  Voir lieu
                </button>
              ) : (
                '—'
              ),
            },
          ]
        : [
            {
              key: 'noVenue',
              label: 'Lieu',
              value: <span className="text-slate-500">Aucun lieu défini</span>,
            },
          ],
    },
    {
      key: 'stats',
      label: 'Statistiques',
      rows: [
        {
          key: 'singersCount',
          label: 'Chanteurs',
          value: event ? (
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faUsers} className="text-purple-400" />
              <span className="text-purple-300 font-bold">{event.singersCount}</span>
            </div>
          ) : (
            '—'
          ),
        },
        {
          key: 'songsCount',
          label: 'Chansons',
          value: event ? (
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faMusic} className="text-cyan-400" />
              <span className="text-cyan-300 font-bold">{event.songsCount}</span>
            </div>
          ) : (
            '—'
          ),
        },
        {
          key: 'updatedAt',
          label: 'Dernière mise à jour',
          value: event ? new Date(event.updatedAt).toLocaleDateString('fr-FR') : '—',
        },
      ],
    },
  ]

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
        <p className="text-red-300 font-mono text-sm">
          <span className="text-red-500">{'[ERROR]'}</span> {error}
        </p>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="p-8 text-center text-slate-400 font-mono">
        {'$ '} Événement non trouvé
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/events')}
          className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition mb-4"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('common.backHome')}
        </button>
        <h1 className="text-4xl font-black bg-linear-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
          {event.name}
        </h1>
        <p className="text-slate-400 mt-2 font-mono text-sm">
          {'> '} <span className="text-amber-400">event</span>{' --details --id='}<span className="text-cyan-400">{event.id.slice(-8)}</span>
        </p>
      </div>

      {/* Detail Sections */}
      {detailSections.map((section) => (
        <div key={section.key} className="bg-slate-900 border border-slate-800 rounded-xl p-8">
          <h2 className="text-xl font-bold text-white mb-6">{section.label}</h2>
          <div className="grid grid-cols-2 gap-6">
            {section.rows.map((row) => (
              <div key={row.key}>
                <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase">
                  {row.label}
                </label>
                <div className="text-slate-300 font-mono">{row.value || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Actions Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
        <h2 className="text-xl font-bold text-white mb-6 font-mono">
          {'> '}<span className="text-cyan-400">actions</span>
        </h2>

        {/* Error message */}
        {error && (
          <div className="mb-4 bg-red-900/30 border border-red-500/50 rounded-lg p-4">
            <p className="text-red-300 font-mono text-sm">
              <span className="text-red-500">{'[ERROR]'}</span> {error}
            </p>
          </div>
        )}

        <div className="space-y-4">
          {/* Status Change Buttons */}
          <div>
            <label className="block text-xs font-mono font-bold text-cyan-400 mb-3 uppercase">
              Modifier le statut
            </label>
            <div className="flex flex-wrap gap-3">
              {event.status !== 'draft' && (
                <button
                  onClick={() => handleStatusChange('draft')}
                  disabled={modalState.isLoading}
                  className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faEdit} />
                  Passer en Brouillon
                </button>
              )}
              {event.status !== 'active' && (
                <button
                  onClick={() => handleStatusChange('active')}
                  disabled={modalState.isLoading}
                  className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faPlay} />
                  Activer
                </button>
              )}
              {event.status !== 'completed' && (
                <button
                  onClick={() => handleStatusChange('completed')}
                  disabled={modalState.isLoading}
                  className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faCheck} />
                  Marquer comme Terminé
                </button>
              )}
              {event.status !== 'cancelled' && (
                <button
                  onClick={() => handleStatusChange('cancelled')}
                  disabled={modalState.isLoading}
                  className="flex items-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faStop} />
                  Annuler
                </button>
              )}
              {event.status !== 'hidden' && (
                <button
                  onClick={() => handleStatusChange('hidden')}
                  disabled={modalState.isLoading}
                  className="flex items-center gap-2 px-4 py-2.5 bg-yellow-600 hover:bg-yellow-500 text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faEyeSlash} />
                  Masquer
                </button>
              )}
            </div>
            <p className="text-slate-400 text-xs mt-2 font-mono">
              Statut actuel : <StatusBadge status={event.status} />
            </p>
          </div>

          {/* Delete Button */}
          <div className="pt-4 border-t border-slate-700/50">
            <label className="block text-xs font-mono font-bold text-red-400 mb-3 uppercase">
              Actions destructives
            </label>
            <button
              onClick={handleDelete}
              disabled={modalState.isLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faTrash} />
              Supprimer l'événement
            </button>
            <p className="text-slate-500 text-xs mt-2 font-mono">
              ⚠️ Cette action est irréversible. Toutes les données associées seront supprimées.
            </p>
          </div>
        </div>
      </div>

      {/* Singers List Section */}
      {event.singers && event.singers.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white font-mono">
                  {'> '}
                  <span className="text-purple-400">singers</span>
                  {' --participated'}
                </h2>
                <p className="text-slate-400 text-xs mt-1 font-mono">
                  Liste des chanteurs ayant participé ({event.singers.length})
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700/50">
                  <th className="px-6 py-3 text-left font-mono text-purple-400 text-xs uppercase tracking-wider">
                    Nom / Pseudo
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-purple-400 text-xs uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-purple-400 text-xs uppercase tracking-wider">
                    Chansons ajoutées
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-purple-400 text-xs uppercase tracking-wider">
                    Date de participation
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {event.singers.map((singer) => (
                  <tr
                    key={singer.id}
                    onClick={() => navigate(`/singers/${singer.id}`)}
                    className="hover:bg-slate-800/50 transition group cursor-pointer"
                  >
                    <td className="px-6 py-4 text-slate-200 font-semibold">
                      {singer.name}
                      {singer.name !== singer.pseudo && (
                        <span className="text-slate-400 text-xs ml-2">({singer.pseudo})</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {singer.isGuest ? (
                        <span className="px-2 py-1 bg-purple-900/30 text-purple-300 rounded text-xs font-mono">
                          Invité
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-cyan-900/30 text-cyan-300 rounded text-xs font-mono">
                          Inscrit
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faMicrophone} className="text-purple-400 text-sm" />
                        <span className="text-purple-300 font-bold">{singer.songsCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {new Date(singer.joinedAt).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Songs List Section */}
      {event.songs && event.songs.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white font-mono">
                  {'> '}
                  <span className="text-cyan-400">songs</span>
                  {' --queue'}
                </h2>
                <p className="text-slate-400 text-xs mt-1 font-mono">
                  Liste des chansons dans la queue ({event.songs.length})
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700/50">
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Titre
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Artiste
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Ajouté le
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {event.songs.map((song) => (
                  <tr key={song.id} className="hover:bg-slate-800/50 transition">
                    <td className="px-6 py-4 text-slate-300 font-mono text-sm font-bold">
                      #{song.position}
                    </td>
                    <td className="px-6 py-4 text-slate-200 font-semibold">{song.title}</td>
                    <td className="px-6 py-4 text-slate-300">{song.artist}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded text-xs font-bold ${
                          song.status === 'played'
                            ? 'bg-green-900/50 text-green-300 border border-green-700/50'
                            : song.status === 'playing'
                            ? 'bg-blue-900/50 text-blue-300 border border-blue-700/50'
                            : 'bg-slate-700/50 text-slate-300 border border-slate-600/50'
                        }`}
                      >
                        {song.status === 'played' ? 'Joué' : song.status === 'playing' ? 'En cours' : 'En attente'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {new Date(song.createdAt).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={modalState.isOpen}
        title={
          modalState.action === 'delete'
            ? 'Supprimer l\'événement'
            : modalState.action === 'status'
            ? 'Modifier le statut'
            : 'Confirmation'
        }
        message={
          modalState.action === 'delete'
            ? `Êtes-vous sûr de vouloir supprimer l'événement "${event.name}" ? Cette action est irréversible et supprimera toutes les données associées (chanteurs, chansons, votes, etc.).`
            : modalState.action === 'status' && modalState.targetStatus
            ? `Voulez-vous vraiment passer l'événement "${event.name}" au statut "${modalState.targetStatus === 'draft' ? 'Brouillon' : modalState.targetStatus === 'active' ? 'Actif' : modalState.targetStatus === 'completed' ? 'Terminé' : modalState.targetStatus === 'cancelled' ? 'Annulé' : 'Masqué'}" ?`
            : 'Confirmer cette action ?'
        }
        confirmLabel={
          modalState.action === 'delete'
            ? 'Oui, supprimer'
            : modalState.action === 'status'
            ? 'Oui, modifier'
            : 'Confirmer'
        }
        cancelLabel="Annuler"
        variant={modalState.action === 'delete' ? 'danger' : 'warning'}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        isLoading={modalState.isLoading}
      />
    </div>
  )
}
